import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PartnerInfo {
  id: string;
  name: string;
  email: string;
  referralCode: string;
  role: string;
}

interface PartnerSession {
  partner: PartnerInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (token: string) => Promise<boolean>;
  logout: () => void;
}

const PARTNER_SESSION_KEY = 'partner_session_token';
const PARTNER_INFO_KEY = 'partner_info';

export function usePartnerSession(): PartnerSession {
  const [partner, setPartner] = useState<PartnerInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedToken = localStorage.getItem(PARTNER_SESSION_KEY);
      const storedInfo = localStorage.getItem(PARTNER_INFO_KEY);

      if (storedToken && storedInfo) {
        try {
          // Verify the token is still valid
          const { data, error } = await supabase.functions.invoke('verify-partner-login', {
            body: { token: storedToken }
          });

          if (error || data?.error) {
            // Token invalid, clear session
            localStorage.removeItem(PARTNER_SESSION_KEY);
            localStorage.removeItem(PARTNER_INFO_KEY);
          } else if (data?.partner) {
            setPartner(data.partner);
            // Update stored info in case it changed
            localStorage.setItem(PARTNER_INFO_KEY, JSON.stringify(data.partner));
          }
        } catch (err) {
          console.error('Error verifying partner session:', err);
          // Don't clear on network errors - use stored info
          setPartner(JSON.parse(storedInfo));
        }
      }
      setIsLoading(false);
    };

    checkSession();
  }, []);

  const login = useCallback(async (token: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-partner-login', {
        body: { token }
      });

      if (error) {
        console.error('Login verification error:', error);
        setIsLoading(false);
        return false;
      }

      if (data?.error) {
        console.error('Login error:', data.error);
        setIsLoading(false);
        return false;
      }

      if (data?.sessionToken && data?.partner) {
        localStorage.setItem(PARTNER_SESSION_KEY, data.sessionToken);
        localStorage.setItem(PARTNER_INFO_KEY, JSON.stringify(data.partner));
        setPartner(data.partner);
        setIsLoading(false);
        return true;
      }

      setIsLoading(false);
      return false;
    } catch (err) {
      console.error('Login exception:', err);
      setIsLoading(false);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(PARTNER_SESSION_KEY);
    localStorage.removeItem(PARTNER_INFO_KEY);
    setPartner(null);
  }, []);

  return {
    partner,
    isLoading,
    isAuthenticated: !!partner,
    login,
    logout
  };
}
